import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { ClearableTimeInput } from './ClearableTimeInput';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ENABLE_RECURRING_UI } from '@/lib/activities/featureFlags';
import { OccurrenceDatePicker } from './OccurrenceDatePicker';
import type { EventFormData } from './eventFormSchema';
import { useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

type TimeMode = 'date_time' | 'open' | 'anytime' | 'season';

interface WhenSectionProps {
  form: UseFormReturn<EventFormData>;
  requiresDate?: boolean;
}

/**
 * Simplified "When" section for the Activity Composer (v1).
 *
 * v1 UI exposes two canonical modes: date_time and open.
 * Season stays data-supported as legacy and shows read-only banner.
 */
export function WhenSection({ form, requiresDate = true }: WhenSectionProps) {
  const timingMode = form.watch('timing_mode') as TimeMode;
  const eventType = form.watch('event_type');
  const isRecurring = form.watch('is_recurring');
  const isMultiDay = form.watch('is_multi_day');
  const startTime = form.watch('start_time');
  const endTime = form.watch('end_time');
  const endsNextDay = form.watch('ends_next_day');
  const isCourse = eventType === 'course';
  // For courses, always show recurring toggle regardless of feature flag
  const showRecurringToggle = isCourse || ENABLE_RECURRING_UI;

  const isLegacySeasonMode = timingMode === 'season';

  const endTimeBeforeStart = startTime && endTime && endTime < startTime;

  // Auto-clear ends_next_day when multi-day is enabled or times change
  useEffect(() => {
    if (isMultiDay && endsNextDay) {
      form.setValue('ends_next_day', false);
    }
  }, [isMultiDay, endsNextDay, form]);

  useEffect(() => {
    if (!endTimeBeforeStart && endsNextDay) {
      form.setValue('ends_next_day', false);
    }
  }, [endTimeBeforeStart, endsNextDay, form]);

  // Canonical migration: legacy anytime is treated as open in editor UI.
  useEffect(() => {
    if (timingMode === 'anytime') {
      form.setValue('timing_mode', 'open', { shouldDirty: false, shouldTouch: false });
    }
  }, [timingMode, form]);

  if (!requiresDate) return null;

  // Legacy season mode banner — block time editing
  if (isLegacySeasonMode) {
    return (
      <Alert className="border-border bg-muted/40">
        <Info className="h-4 w-4 text-muted-foreground" />
        <AlertDescription className="text-sm text-muted-foreground">
          Den här aktiviteten använder ett äldre säsongsläge som inte kan redigeras här. Skapa en ny aktivitet med datum/tid eller öppet-läge om du vill ändra.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="timing_mode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tidsupplägg</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value === 'anytime' ? 'open' : field.value}
                onValueChange={(value) => {
                  field.onChange(value as TimeMode);
                  if (value === 'open') {
                    form.setValue('start_time', '', { shouldDirty: true });
                    form.setValue('end_time', '', { shouldDirty: true });
                    form.setValue('event_date', '', { shouldDirty: true });
                    form.setValue('is_multi_day', false, { shouldDirty: true });
                    form.setValue('ends_next_day', false, { shouldDirty: true });
                    form.setValue('is_recurring', false, { shouldDirty: true });
                    form.setValue('selected_dates', [], { shouldDirty: true });
                  }
                }}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="date_time" id="timing-date-time" />
                  <Label htmlFor="timing-date-time">Ett datum & tid</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="open" id="timing-open" />
                  <Label htmlFor="timing-open">Öppet / kom förbi</Label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormDescription>
              Välj om aktiviteten har en fast tid eller är öppen att besöka.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {timingMode === 'open' && (
        <div className="space-y-4">
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Från datum</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormDescription>Valfritt — lämna tomt för “Öppet under säsong”.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Till datum</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormDescription>Valfritt — använd om du vill visa ett datumintervall.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}

      {timingMode === 'date_time' && !isRecurring && (
        <div className="space-y-4">
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="event_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Datum *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Starttid *</FormLabel>
                  <FormControl>
                    <ClearableTimeInput data-testid="event-start-time" value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sluttid</FormLabel>
                  <FormControl>
                    <ClearableTimeInput data-testid="event-end-time" value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">Valfritt – standard +2h</p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Multi-day toggle — directly under end time */}
          <FormField
            control={form.control}
            name="is_multi_day"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-background px-4 py-3">
                <div className="space-y-1">
                  <FormLabel className="text-sm font-medium">Pågår över flera dagar?</FormLabel>
                  <FormDescription className="text-xs">Till exempel kurs, retreat eller helgaktivitet</FormDescription>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />

          {isMultiDay && (
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slutdatum *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Ends next day hint (only without multi-day) */}
          {!isMultiDay && endTimeBeforeStart && (
            <FormField
              control={form.control}
              name="ends_next_day"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border p-3 bg-muted/50">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-0.5 leading-none">
                    <FormLabel className="text-sm">Slutar nästa dag</FormLabel>
                    <FormDescription className="text-xs">
                      T.ex. kvällsevent som slutar 01:00
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          )}
        </div>
      )}

      {/* Recurring toggle — always shown for courses, otherwise feature-flagged */}
      {timingMode === 'date_time' && showRecurringToggle && !isRecurring && (
        <FormField
          control={form.control}
          name="is_recurring"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel className="text-sm">{isCourse ? 'Flera tillfällen (serie)' : 'Återkommande'}</FormLabel>
                <FormDescription className="text-xs">
                  {isCourse ? 'Kursen har flera kurstillfällen' : 'Aktiviteten körs på flera datum'}
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {/* Course series info box — shown when course + recurring is on */}
      {timingMode === 'date_time' && isCourse && isRecurring && (
        <Alert className="border-border bg-muted/40">
          <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Flera tillfällen i samma kurs</p>
            <AlertDescription className="text-xs text-muted-foreground mt-1">
              Alla tillfällen hör till samma kurs och samma deltagare. Bokningen gäller hela kursen.
              <br />
              Vill du erbjuda kursen igen vid ett annat startdatum? Skapa en ny kurs.
            </AlertDescription>
          </div>
        </Alert>
      )}

      {timingMode === 'date_time' && isRecurring && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Starttid (gemensam) *</FormLabel>
                  <FormControl>
                    <ClearableTimeInput value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sluttid (gemensam)</FormLabel>
                  <FormControl>
                    <ClearableTimeInput value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">Valfritt – standard +2h</p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="selected_dates"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Välj datum *</FormLabel>
                <FormControl>
                  <OccurrenceDatePicker
                    selectedDates={field.value}
                    onDatesChange={field.onChange}
                    startTime={startTime}
                    endTime={endTime}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
